import { z } from 'zod';

const BASE = '/api';

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function call<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    credentials: 'include',
  });
  const text = await res.text();
  const json = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = extractError(json);
    throw new ApiError(res.status, err.code ?? 'UNKNOWN', err.message ?? `HTTP ${res.status}`);
  }
  return schema.parse(json);
}

function extractError(json: unknown): { code?: string; message?: string } {
  if (json && typeof json === 'object' && 'error' in json) {
    const e = (json as { error?: unknown }).error;
    if (e && typeof e === 'object') {
      const o = e as { code?: unknown; message?: unknown };
      return {
        code: typeof o.code === 'string' ? o.code : undefined,
        message: typeof o.message === 'string' ? o.message : undefined,
      };
    }
  }
  return {};
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const userListItemSchema = z.object({
  id: z.string(),
  telegramId: z.number(),
  telegramUsername: z.string().nullable(),
  displayName: z.string(),
  isDriver: z.boolean(),
  isSuspended: z.boolean(),
  suspendedUntil: z.number().nullable(),
  ridesCompleted: z.number(),
  ratingAvg: z.number().nullable(),
  ratingCount: z.number(),
  reportCountOpen: z.number(),
  createdAt: z.number(),
});
export type UserListItem = z.infer<typeof userListItemSchema>;

export const userDetailSchema = userListItemSchema.extend({
  premiumUntil: z.number().nullable(),
  bio: z.string().nullable(),
  locale: z.string(),
  countryCode: z.string(),
  carMake: z.string().nullable(),
  carModel: z.string().nullable(),
  carColor: z.string().nullable(),
  carPlate: z.string().nullable(),
  counts: z.object({
    rides: z.number(),
    bookingsAsPassenger: z.number(),
    bookingsAsDriver: z.number(),
    ratingsGiven: z.number(),
    ratingsReceived: z.number(),
    reports: z.number(),
    notifications: z.number(),
  }),
});
export type UserDetail = z.infer<typeof userDetailSchema>;

export const pricingConfigSchema = z.object({
  id: z.string(),
  countryCode: z.string(),
  fuelPricePerL: z.number(),
  consumptionLPer100Km: z.number(),
  splitFactor: z.number(),
  tollPerKm: z.number(),
  maxOverpriceRatio: z.number(),
  reviewOverpriceRatio: z.number(),
});
export type PricingConfig = z.infer<typeof pricingConfigSchema>;

export const recomputePreviewSchema = z.object({
  changes: z.array(
    z.object({
      routeId: z.string(),
      label: z.string(),
      distanceKm: z.number(),
      from: z.number(),
      to: z.number(),
    }),
  ),
});
export type RecomputePreview = z.infer<typeof recomputePreviewSchema>;

export const recomputeApplySchema = z.object({
  scanned: z.number(),
  updated: z.number(),
  changes: z.array(z.object({ routeId: z.string(), from: z.number(), to: z.number() })),
});

export const antiAbuseFlagSchema = z.object({
  id: z.string(),
  userId: z.string(),
  userDisplayName: z.string().nullable(),
  rideId: z.string().nullable(),
  ruleId: z.string(),
  severity: z.number(),
  details: z.record(z.unknown()),
  resolved: z.boolean(),
  resolutionNote: z.string().nullable(),
  createdAt: z.number(),
});
export type AntiAbuseFlag = z.infer<typeof antiAbuseFlagSchema>;

export const reportSchema = z.object({
  id: z.string(),
  reporterId: z.string(),
  reporterDisplayName: z.string().nullable(),
  reportedUserId: z.string().nullable(),
  reportedDisplayName: z.string().nullable(),
  rideId: z.string().nullable(),
  bookingId: z.string().nullable(),
  reason: z.string(),
  description: z.string().nullable(),
  status: z.string(),
  resolutionNote: z.string().nullable(),
  createdAt: z.number(),
});
export type Report = z.infer<typeof reportSchema>;

export const metricsSchema = z.object({
  totalUsers: z.number(),
  totalDrivers: z.number(),
  totalSuspended: z.number(),
  ridesPublished: z.number(),
  ridesToday: z.number(),
  bookingsToday: z.number(),
  bookingsPending: z.number(),
  premiumActive: z.number(),
  openAntiAbuseFlags: z.number(),
  openReports: z.number(),
  failedNotifications: z.number(),
  lastNotificationDrainAt: z.number().nullable(),
});
export type Metrics = z.infer<typeof metricsSchema>;

export const auditLogItemSchema = z.object({
  id: z.string(),
  actorEmail: z.string().nullable(),
  action: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  details: z.string().nullable(),
  createdAt: z.number(),
});
export type AuditLogItem = z.infer<typeof auditLogItemSchema>;

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const getMetrics = () => call('/admin/metrics', metricsSchema);

export const searchUsers = (q: string) =>
  call(
    `/admin/users?q=${encodeURIComponent(q)}`,
    z.object({ items: z.array(userListItemSchema) }),
  );

export const getUserDetail = (id: string) => call(`/admin/users/${id}`, userDetailSchema);

export const suspendUser = (id: string, body: { untilMs: number | null; reason: string }) =>
  call(`/admin/users/${id}/suspend`, z.object({ ok: z.boolean() }), {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const unsuspendUser = (id: string) =>
  call(`/admin/users/${id}/unsuspend`, z.object({ ok: z.boolean() }), { method: 'POST' });

export const wipeUser = (id: string, confirm: string) =>
  call(
    `/admin/users/${id}`,
    z.object({
      ok: z.boolean(),
      deleted: z.record(z.number()),
    }),
    { method: 'DELETE', body: JSON.stringify({ confirm }) },
  );

export const getPricing = () =>
  call('/admin/pricing', z.object({ items: z.array(pricingConfigSchema) }));

export const updatePricing = (countryCode: string, body: Partial<PricingConfig>) =>
  call(`/admin/pricing/${countryCode}`, pricingConfigSchema, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const previewRecompute = () => call('/admin/recompute-preview', recomputePreviewSchema);

export const applyRecompute = () =>
  call('/admin/recompute-routes', recomputeApplySchema, { method: 'POST' });

export const listAntiAbuseFlags = (resolved: boolean) =>
  call(
    `/admin/anti-abuse-flags?resolved=${resolved}`,
    z.object({ items: z.array(antiAbuseFlagSchema) }),
  );

export const resolveAntiAbuseFlag = (id: string, note: string) =>
  call(`/admin/anti-abuse-flags/${id}/resolve`, z.object({ ok: z.boolean() }), {
    method: 'POST',
    body: JSON.stringify({ note }),
  });

export const listReports = (status: 'open' | 'resolved') =>
  call(`/admin/reports?status=${status}`, z.object({ items: z.array(reportSchema) }));

export const resolveReport = (
  id: string,
  body: { action: 'dismiss' | 'warn' | 'suspend'; note: string; suspendUntilMs?: number | null },
) =>
  call(`/admin/reports/${id}/resolve`, z.object({ ok: z.boolean() }), {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const listAuditLog = (limit = 50) =>
  call(`/admin/audit-log?limit=${limit}`, z.object({ items: z.array(auditLogItemSchema) }));

export const getCurrentAdmin = () =>
  call('/auth/me', z.object({ email: z.string(), name: z.string().nullable() }));

export const login = (email: string, password: string) =>
  call('/auth/login', z.object({ email: z.string() }), {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

export const logout = () =>
  call('/auth/logout', z.object({ ok: z.boolean() }), { method: 'POST' });
